import { useState } from 'react';
import axios from 'axios';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Check, Copy, KeyRound } from 'lucide-react';
import { config } from '../../config';
import { useConfirm } from '@/hooks/useConfirm';
import { StepUpChallenge } from '@/features/auth/StepUpChallenge';
import { isStepUpRequired, useStepUp } from '@/features/auth/stepUp';
import { apiErrorMessage } from '@/utils/apiError';
import { passwordGroups } from './consolePassword';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

/** What VyOS says about the console login and SSH server. */
interface ConsoleState {
  sshEnabled: boolean;
  /** The login still accepts the factory password: the gateway's own replacement failed. */
  defaultPassword: boolean;
}

interface ConsoleResponse {
  supported: boolean;
  message: string;
  state: ConsoleState | null;
}

interface ConsolePasswordResponse {
  password: string;
  state: ConsoleState | null;
}

/**
 * The appliance's console login and SSH server (gateway#87).
 *
 * There is no stored password to show. Change console password makes a new one on the
 * server, the gateway applies it, and this shows it once; closing the dialog is the last
 * time anyone sees it. A VyOS appliance only: a container gateway has no console login.
 */
export function ApplianceConsoleCard({ gatewayId }: { gatewayId: string }) {
  const backendApi = config.backendApi;
  const queryClient = useQueryClient();
  const queryKey = ['gateway-console', gatewayId, backendApi];
  const { confirm, confirmDialog } = useConfirm();
  const { challenge, satisfy, clear } = useStepUp();
  const [error, setError] = useState<string | undefined>(undefined);
  const [shown, setShown] = useState<string | undefined>(undefined);
  const [copied, setCopied] = useState(false);

  const { data, isLoading, error: readError } = useQuery<ConsoleResponse>({
    queryKey,
    queryFn: async () =>
      (await axios.get<ConsoleResponse>(`${backendApi}/api/gateways/${gatewayId}/console`, { withCredentials: true }))
        .data,
  });

  const changePassword = useMutation({
    mutationFn: async () =>
      (
        await axios.post<ConsolePasswordResponse>(
          `${backendApi}/api/gateways/${gatewayId}/console/password`,
          {},
          { withCredentials: true },
        )
      ).data,
    onSuccess: (result) => {
      setError(undefined);
      setCopied(false);
      setShown(result.password);
      queryClient.setQueryData<ConsoleResponse>(queryKey, (old) =>
        old ? { ...old, state: result.state ?? old.state } : old,
      );
    },
    onError: (e: unknown) => {
      const c = isStepUpRequired(e);
      if (c) {
        satisfy(c);
        return;
      }
      setError(apiErrorMessage(e, 'Could not change the console password.'));
    },
  });

  const setSsh = useMutation({
    mutationFn: async (enabled: boolean) =>
      (
        await axios.post<ConsoleResponse>(
          `${backendApi}/api/gateways/${gatewayId}/console/ssh`,
          { enabled },
          { withCredentials: true },
        )
      ).data,
    onSuccess: (result) => {
      setError(undefined);
      queryClient.setQueryData<ConsoleResponse>(queryKey, result);
    },
    onError: (e: unknown) => {
      setError(apiErrorMessage(e, 'Could not change SSH.'));
    },
  });

  const toggleSsh = async (enabled: boolean) => {
    const ok = await confirm(
      enabled
        ? {
            title: 'Turn on SSH',
            message:
              "The gateway's SSH server will listen on its network addresses. An address that keeps failing to log in is blocked for a while.",
            confirmText: 'Turn on SSH',
          }
        : {
            title: 'Turn off SSH',
            message: 'Open SSH sessions to the gateway will be cut off. The console still works.',
            confirmText: 'Turn off SSH',
            isDanger: true,
          },
    );
    if (ok) setSsh.mutate(enabled);
  };

  const copy = async () => {
    if (!shown) return;
    try {
      await navigator.clipboard.writeText(shown);
      setCopied(true);
    } catch {
      // A browser that refuses the clipboard still shows the password to read off.
    }
  };

  const closeShown = () => {
    setShown(undefined);
    setCopied(false);
  };

  if (data && !data.supported) return null;

  const state = data?.state ?? null;
  const busy = changePassword.isPending || setSsh.isPending;

  return (
    <div className="rounded-md border border-border-subtle bg-card p-3 space-y-2" data-testid="appliance-console">
      <div className="flex items-center gap-2 text-sm font-medium">
        <KeyRound className="w-4 h-4 text-muted-foreground" />
        Console login
        {state ? (
          <Badge
            className={
              state.sshEnabled
                ? 'bg-sev-4/15 text-sev-4 border-0 font-normal'
                : 'bg-muted text-muted-foreground border-0 font-normal'
            }
          >
            SSH {state.sshEnabled ? 'on' : 'off'}
          </Badge>
        ) : null}
      </div>

      {isLoading ? <p className="text-xs text-muted-foreground">Reading the console login…</p> : null}
      {readError ? (
        <p className="text-xs text-muted-foreground">
          {apiErrorMessage(readError, "Couldn't read the console login from the gateway.")}
        </p>
      ) : null}
      {data && !state && data.message ? <p className="text-xs text-muted-foreground">{data.message}</p> : null}

      {state?.defaultPassword ? (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            The console still accepts the factory password. Change it now.
          </AlertDescription>
        </Alert>
      ) : null}

      <p className="text-xs text-muted-foreground">
        The user is <span className="font-mono">vyos</span>. Witina never stores this password: change it to get one
        you can use.
      </p>

      {error ? (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      ) : null}

      <div className="flex flex-wrap gap-2">
        <Button size="sm" variant="outline" disabled={busy || !!readError} onClick={() => { changePassword.mutate(); }}>
          Change console password
        </Button>
        {state ? (
          <Button size="sm" variant="outline" disabled={busy} onClick={() => void toggleSsh(!state.sshEnabled)}>
            {state.sshEnabled ? 'Turn off SSH' : 'Turn on SSH'}
          </Button>
        ) : null}
      </div>

      {challenge ? (
        <StepUpChallenge
          challenge={challenge}
          onSatisfied={() => {
            clear();
            changePassword.mutate();
          }}
          // Backing out abandons the change. Re-running it would draw a fresh refusal and
          // reopen this dialog.
          onCancel={clear}
        />
      ) : null}

      <Dialog open={!!shown} onOpenChange={(open) => { if (!open) closeShown(); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>New console password</DialogTitle>
            <DialogDescription>
              Log in as <span className="font-mono">vyos</span> with this password. It won&apos;t be shown again:
              Witina doesn&apos;t keep it.
            </DialogDescription>
          </DialogHeader>
          {/* Groups are spacing, not characters: the password has no spaces in it. */}
          <p className="flex flex-wrap gap-3 font-mono text-lg tracking-wider" data-testid="console-password">
            {passwordGroups(shown ?? '').map((group, i) => (
              <span key={i}>{group}</span>
            ))}
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => void copy()} className="gap-2">
              {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
              {copied ? 'Copied' : 'Copy'}
            </Button>
            <Button onClick={closeShown}>Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {confirmDialog}
    </div>
  );
}
